import React, { useState } from "react";
import {
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDeepCompareEffect } from "@fuse/hooks";
import FuseLoading from "@fuse/core/FuseLoading";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import {
  getReportById,
  selectReportById,
} from "../../reports/store/reportSlice";
import { ApiUserAdmin, endpoints_auditAdmin } from "app/api/userAdminApi";
import useIsMobile from "../../../hooks/useIsMobile";

const categorys = [
  { nombre: "INSTALACIONES", rango: [1, 16] },
  { nombre: "LIMPIEZA Y SANITIZACIÓN", rango: [17, 20] },
  { nombre: "CONTROL DE PLAGAS", rango: [21, 23] },
  { nombre: "HIGIENE DEL PERSONAL", rango: [24, 26] },
  { nombre: "CAPACITACIÓN", rango: [27, 28] },
  { nombre: "MATERIAS PRIMAS", rango: [29, 33] },
  { nombre: "PROCESOS Y PRODUCTO TERMINADO", rango: [34, 39] },
];

function groupedByCategory(grouped) {
  return categorys.map((cat) => {
    const items = Object.keys(grouped)
      .map((num) => parseInt(num, 10))
      .filter((num) => num >= cat.rango[0] && num <= cat.rango[1])
      .sort((a, b) => a - b)
      .map((num) => ({
        number: num,
        question: grouped[num],
      }));
    return { ...cat, items };
  });
}

const api = new ApiUserAdmin();

const ViewReport = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [answersByCategory, setAnswersByCategory] = useState([]);
  const [page, setPage] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const { reportId } = params;
  const report = useSelector((state) => selectReportById(state, reportId));

  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (report) {
      const regex = /^\s*(PUNTAJE|OBSERVACIONES|PRIORIDAD)\s+(\d+)\.-/;
      const grouped = report.answers.reduce((acc, item) => {
        const cleanName = item.question_name.trim();
        const match = cleanName.match(regex);
        if (match) {
          const num = match[2];
          if (!acc[num]) acc[num] = [];
          acc[num].push({ ...item, question_name: cleanName });
        }
        return acc;
      }, {});
      const result = groupedByCategory(grouped);
      setAnswersByCategory(result);
    }
  }, [report]);

  // Generar PDF automáticamente cuando los datos estén listos
  React.useEffect(() => {
    const generatePdf = async () => {
      if (report && answersByCategory.length > 0 && !pdfUrl && !loadingPdf) {
        setLoadingPdf(true);
        try {
          const promedioCumplimiento = calculateTotalCompliancePercentage(answersByCategory);
          const body = { 
            audit: report, 
            categories: answersByCategory,
            promedioCumplimiento: promedioCumplimiento
          };
          console.log("🚀 ~ generatePdf ~ body:", body)
          const pdf = await api.postBlob(endpoints_auditAdmin.EXPORT_BPM, body);
          const url = window.URL.createObjectURL(pdf);
          setPdfUrl(url);
        } catch (error) {
          console.error("Error al generar el PDF:", error);
        } finally {
          setLoadingPdf(false);
        }
      }
    };
    generatePdf();
  }, [report, answersByCategory]);

  // Limpiar la URL del PDF cuando el componente se desmonte
  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useDeepCompareEffect(() => {
    dispatch(getReportById(reportId));
  }, [dispatch]);

  if (!report) return <FuseLoading />;

  const currentCategory = answersByCategory[page] || null;

  const getByType = (arr, type) => {
    const found = arr.find((a) => a.question_name.startsWith(type));
    return found ? found.question_value || "" : "";
  };

  const getColorByPuntaje = (value) => {
    switch (value) {
      case "P3":
        return "#93d150";
      case "P2":
        return "#ffff00";
      case "P1":
        return "#ff0000";
      default:
        return "#ffffff";
    }
  };

  const calculateCategoryScore = (items) => {
    let sum = 0;
    let countValid = 0;
    items.forEach((item) => {
      const puntaje = getByType(item.question, "PUNTAJE");
      if (["0", "1", "2"].includes(puntaje)) {
        sum += parseInt(puntaje, 10);
        countValid++;
      }
    });
    const max = countValid * 2;
    const percentage = max > 0 ? Math.round((sum / max) * 100) : 0;
    return { sum, max, percentage };
  };

  const calculateTotalCompliancePercentage = (categories) => {
    let totalSum = 0;
    let totalMax = 0;
    categories.forEach((category) => {
      const score = calculateCategoryScore(category.items);
      totalSum += score.sum;
      totalMax += score.max;
    });
    const percentage = totalMax > 0 ? Math.round((totalSum / totalMax) * 100) : 0;
    return percentage;
  };

  const handleBack = () => history.back();

  return (
    <Box p={3}>
      <Button
        variant="contained"
        color="info"
        size="small"
        startIcon={
          <FuseSvgIcon size={20}>material-solid:arrow_back</FuseSvgIcon>
        }
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        Volver
      </Button>

      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        {report.name}: {report.code}
      </Typography>
      <Typography align="center" variant="subtitle1" gutterBottom>
        Realizada por <strong>{report.user_name}</strong>
      </Typography>

      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          disabled={!pdfUrl || loadingPdf}
          onClick={() => {
            if (pdfUrl) {
              const link = document.createElement("a");
              link.href = pdfUrl;
              link.setAttribute(
                "download",
                `reporte_${report.id || "auditoria"}.pdf`
              );
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }}
        >
          {loadingPdf ? "Generando PDF..." : "Descargar PDF"}
        </Button>
      </Box>

      {/* Visor de PDF integrado */}
      {pdfUrl && (
        <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            Vista Previa del Reporte
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: isMobile ? "400px" : "600px",
              border: "1px solid #ccc",
              borderRadius: 1,
            }}
          >
            <iframe
              src={pdfUrl}
              title="Vista previa del PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          </Box>
        </Paper>
      )}

      {loadingPdf && (
        <Box display="flex" justifyContent="center" mb={3}>
          <Typography variant="body1" color="primary">
            Generando PDF automáticamente...
          </Typography>
        </Box>
      )}

       { /* currentCategory && ( *
      //   <Paper elevation={3} sx={{ p: 3 }}>
      //     <Typography variant="h5" align="center" sx={{ fontWeight: "bold" }}>
      //       {currentCategory.nombre}
      //     </Typography>
      //
      //     {isMobile ? (
      //       // Vista móvil: cards verticales
      //       <Stack spacing={2} mt={3}>
      //         {currentCategory.items.map((item) => {
      //           const prioridad = getByType(item.question, "PRIORIDAD");
      //           return (
      //             <Paper
      //               key={item.number}
      //               sx={{
      //                 p: 2,
      //                 borderLeft: `6px solid ${getColorByPuntaje(prioridad)}`,
      //               }}
      //             >
      //               <Typography fontWeight="bold">
      //                 Número: {item.number}
      //               </Typography>
      //               <Typography>
      //                 Puntaje: {getByType(item.question, "PUNTAJE")}
      //               </Typography>
      //               <Typography>
      //                 Observaciones: {getByType(item.question, "OBSERVACIONES")}
      //               </Typography>
      //               <Typography>
      //                 Prioridad:{" "}
      //                 <Chip
      //                   size="small"
      //                   variant="outlined"
      //                   label={prioridad}
      //                   sx={{ ml: 1 }}
      //                 />
      //               </Typography>
      //             </Paper>
      //           );
      //         })}
      //       </Stack>
      //     ) : (
      //       // Vista desktop: tabla completa
      //       <Table sx={{ mt: 2 }}>
      //         <TableHead sx={{ backgroundColor: "primary.light" }}>
      //           <TableRow>
      //             <TableCell>Número</TableCell>
      //             <TableCell>PUNTAJE</TableCell>
      //             <TableCell>OBSERVACIONES</TableCell>
      //             <TableCell>PRIORIDAD</TableCell>
      //           </TableRow>
      //         </TableHead>
      //         <TableBody>
      //           {currentCategory.items.map((item) => {
      //             const prioridad = getByType(item.question, "PRIORIDAD");
      //             return (
      //               <TableRow key={item.number}>
      //                 <TableCell>{item.number}</TableCell>
      //                 <TableCell>
      //                   {getByType(item.question, "PUNTAJE")}
      //                 </TableCell>
      //                 <TableCell>
      //                   {getByType(item.question, "OBSERVACIONES")}
      //                 </TableCell>
      //                 <TableCell
      //                   sx={{ backgroundColor: getColorByPuntaje(prioridad) }}
      //                 >
      //                   {prioridad}
      //                 </TableCell>
      //               </TableRow>
      //             );
      //           })}
      //         </TableBody>
      //       </Table>
      //     )}
      //
      //     <Box mt={3} display="flex" justifyContent="space-between">
      //       <Button
      //         variant="contained"
      //         color="secondary"
      //         disabled={page === 0}
      //         onClick={() => setPage((p) => p - 1)}
      //       >
      //         Anterior
      //       </Button>
      //       <Button
      //         variant="contained"
      //         color="primary"
      //         disabled={page === answersByCategory.length - 1}
      //         onClick={() => setPage((p) => p + 1)}
      //       >
      //         Siguiente
      //       </Button>
      //     </Box>
      //   </Paper>
      // )*/}
    </Box>
  );
};

export default ViewReport;
